// Header.jsx
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectAuth, hydrateFromStorage } from "../pages/Auth/slice";
import { useEffect, useMemo, useRef, useCallback, useState } from "react";

function IconHamburger({ className = "" }) {
  return (
    <svg className={className} width="26" height="26" viewBox="0 0 26 26" aria-hidden="true" focusable="false">
      <path d="M4 6h16M4 12h16M4 18h16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconClose({ className = "" }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 6L18 18M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user: userFromStore } = useSelector(selectAuth);
  const isJayeon = process.env.REACT_APP_STYLE === 'jayeon';

  const utilRef = useRef(null); // .util (pc menu wrap)
  const navRef = useRef(null); // nav (pc gnb hover 기준)
  const moWrapRef = useRef(null); // .mo_menu_wrap (mobile menu)
  const moOpenIdxRef = useRef(-1); // 현재 열린 mobile depth2 index

  const [moOpen, setMoOpen] = useState(false);

  // ===== 모바일 depth2 슬라이드 유틸 =====
  const slideOpen = useCallback((el) => {
    if (!el) return;
    el.classList.add("is-open");
    el.style.maxHeight = el.scrollHeight + "px";
  }, []);

  const slideClose = useCallback((el) => {
    if (!el) return;
    el.classList.remove("is-open");
    el.style.maxHeight = "0px";
  }, []);

  const closeMenu = useCallback(() => {
    utilRef.current?.classList.remove("active");
    navRef.current?.classList.remove("active");

    if (moWrapRef.current) {
      moWrapRef.current.classList.remove("active");
      const depth2s = moWrapRef.current.querySelectorAll(".mo_menu_depth2");
      depth2s.forEach((el) => {
        el.classList.remove("is-open");
        el.style.maxHeight = "0px";
      });
    }
    moOpenIdxRef.current = -1;

    setMoOpen(false);

    // 스크롤 복구
    document.body.classList.remove("no-scroll");
  }, []);

  const toggleMenu = useCallback(() => {
    const wrap = moWrapRef.current;
    if (!wrap) return;

    const willOpen = !wrap.classList.contains("active");
    wrap.classList.toggle("active", willOpen);

    setMoOpen(willOpen);

    // 스크롤 방지/복구
    document.body.classList.toggle("no-scroll", willOpen);

    if (!willOpen) {
      moOpenIdxRef.current = -1;
      const depth2s = wrap.querySelectorAll(".mo_menu_depth2");
      depth2s.forEach((el) => {
        el.classList.remove("is-open");
        el.style.maxHeight = "0px";
      });
    }
  }, []);

  const userFromStorage = useMemo(() => {
    try {
      const s = localStorage.getItem("user");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!userFromStore) dispatch(hydrateFromStorage());
  }, [userFromStore, dispatch]);

  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  useEffect(() => {
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, []);

  const user = userFromStore || userFromStorage;
  const username = (user && (user.user_name || user.name || user.username || user.id)) || "사용자";
  const token = localStorage.getItem("token");

  // 슬림이 설정 버튼은 슬림이 접속 키가 발급된 지점에만 노출
  const [slimiEnabled, setSlimiEnabled] = useState(false);
  useEffect(() => {
    if (!isJayeon || !token) {
      setSlimiEnabled(false);
      return;
    }

    let cancelled = false;
    const base = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
    fetch(base + "/api/slimi/branch-admin-exists", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((json) => {
        if (!cancelled) setSlimiEnabled(json?.success === true && json?.data?.exists === true);
      })
      .catch(() => {
        if (!cancelled) setSlimiEnabled(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isJayeon, token]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    dispatch(logout());
    navigate("/login");
  };

  const handleEventListClick = (e) => {
    e.preventDefault();

    const currentToken = localStorage.getItem("token");
    if (!currentToken) {
      alert("로그인이 필요합니다.");
      navigate("/login");
      return;
    }

    // 로컬스토리지: branch_idx, branch_name
    // 외부전송 폼: branch_id, branch_name, area_sort
    const branch_id = user?.branch_id || ""; // branch_idx 값을 branch_id로 사용
    const branch_name = user?.branch_name || "";
    const area_sort = user?.area_sort || ""; // 로컬스토리지에 없으면 빈 값

    // 3. 폼 생성 및 전송
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "https://landing.jayeontotal.com/admin/";
    form.target = "_blank";

    const createInput = (name, value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      return input;
    };

    form.appendChild(createInput("token", currentToken));
    
    // 값이 있을 때만 input 생성 (빈 값이라도 보내야 한다면 조건문 제거 가능)
    if (branch_id) form.appendChild(createInput("branch_id", branch_id));
    if (branch_name) form.appendChild(createInput("branch_name", branch_name));
    if (area_sort) form.appendChild(createInput("area_sort", area_sort));

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);

    closeMenu();
  };

  const handleSlimiClick = (e) => {
    e.preventDefault();

    const currentToken = localStorage.getItem("token");
    if (!currentToken) {
      alert("로그인이 필요합니다.");
      navigate("/login");
      return;
    }

    const branch_id = user?.branch_id || ""; // 지점 영문 코드 (branch_eng)
    if (!branch_id) {
      alert("지점 영문 코드가 없습니다. 관리자에게 문의해주세요.");
      return;
    }

    // 슬림이 지점 화면 (로그인은 슬림이 화면에서 직접)
    window.open(`https://slimi.jayeontotal.com/admin`, "_blank", "noopener");
    closeMenu();
  };

  const isLoginPage = location.pathname === "/login";

  const openGnb = useCallback(() => {
    navRef.current?.classList.add("active");
  }, []);
  const closeGnb = useCallback(() => {
    navRef.current?.classList.remove("active");
  }, []);

  const onToggleMobileDepth = useCallback(
    (idx) => {
      const wrap = moWrapRef.current;
      if (!wrap) return;

      const depth2s = Array.from(wrap.querySelectorAll(".mo_menu_depth2"));
      if (depth2s.length === 0) return;

      const prev = moOpenIdxRef.current;

      if (prev === idx) {
        slideClose(depth2s[idx]);
        moOpenIdxRef.current = -1;
        return;
      }

      if (prev >= 0 && depth2s[prev]) slideClose(depth2s[prev]);
      if (depth2s[idx]) slideOpen(depth2s[idx]);

      moOpenIdxRef.current = idx;
    },
    [slideClose, slideOpen]
  );

  return (
    <header>
      <div className="header">
        <h1>
          <Link to="/profiling" onClick={closeMenu}>
            {process.env.REACT_APP_CLINIC_NAME} AI
          </Link>
        </h1>

        {!isLoginPage && (
          <div className="right">
            {/* mobile menu */}
            <div className="mobile_menu">
              <button
                type="button"
                onClick={toggleMenu}
                aria-label={moOpen ? "모바일 메뉴 닫기" : "모바일 메뉴 열기"}
                aria-expanded={moOpen}
                className="mo_menu_btn"
              >
                <span className="mo_menu_icon" aria-hidden="true">
                  {moOpen ? <IconClose /> : <IconHamburger />}
                </span>
              </button>

              <div className="mo_menu_wrap" ref={moWrapRef}>
                <div>
                  <ul>
                    {slimiEnabled && (
                      <li className="mo_menu_lst">
                        <h3>
                          <a href="#" onClick={handleSlimiClick} style={{ display: "block", color: "inherit" }}>
                            슬림이 설정
                          </a>
                        </h3>
                      </li>
                    )}

                    {/* [추가] 모바일 - 제일 첫 번째 메뉴 */}
                    {isJayeon && (
                      <li className="mo_menu_lst">
                        <h3>
                          <a href="#" onClick={handleEventListClick} style={{ display: "block", color: "inherit" }}>
                            이벤트 신청 리스트
                          </a>
                        </h3>
                      </li>
                    )}

                    <li className="mo_menu_lst">
                      <h3 onClick={() => onToggleMobileDepth(0)}>고객 응대</h3>
                      <ul className="mo_menu_depth2">
                        <li>
                          <Link to="/profiling" onClick={closeMenu}>
                            프로파일링
                          </Link>
                        </li>
                        <li>
                          <Link to="/review" onClick={closeMenu}>
                            리뷰 답변
                          </Link>
                        </li>
                        <li>
                          <Link to="/training-response" onClick={closeMenu}>
                            불만고객응대
                          </Link>
                        </li>
                      </ul>
                    </li>

                    <li className="mo_menu_lst">
                      <h3 onClick={() => onToggleMobileDepth(1)}>트레이닝/코칭</h3>
                      <ul className="mo_menu_depth2">
                        <li>
                          <Link to="/training" onClick={closeMenu}>
                            트레이닝
                          </Link>
                        </li>
                        <li>
                          <Link to="/training-followup" onClick={closeMenu}>
                            트레이닝(후속조치)
                          </Link>
                        </li>
                        <li>
                          <Link to="/simulation" onClick={closeMenu}>
                            시뮬레이션
                          </Link>
                        </li>
                        <li>
                          <Link to="/education" onClick={closeMenu}>
                            교육챗봇
                          </Link>
                        </li>
                      </ul>
                    </li>

                    <li className="mo_menu_lst">
                      <h3 onClick={() => onToggleMobileDepth(2)}>레퍼런스</h3>
                      <ul className="mo_menu_depth2">
                        <li>
                          <Link to="/dictionary" onClick={closeMenu}>
                            백과사전
                          </Link>
                        </li>
                        <li>
                          <Link to="/analysis" onClick={closeMenu}>
                            행동유형분석
                          </Link>
                        </li>
                      </ul>
                    </li>

                    <li className="mo_menu_lst">
                      <h3 onClick={() => onToggleMobileDepth(3)}>상담/운영</h3>
                      <ul className="mo_menu_depth2">
                        <li>
                          <Link to="/charting" onClick={closeMenu}>
                            차팅도우미
                          </Link>
                        </li>
                        <li>
                          <Link to="/smalltalk" onClick={closeMenu}>
                            스몰토크
                          </Link>
                        </li>
                        <li>
                          <Link to="/memotalk" onClick={closeMenu}>
                            메모&알림톡도우미
                          </Link>
                        </li>
                      </ul>
                    </li>
                  </ul>

                  {token && (
                    <div className="btnbox">
                      <Link to="/user_edit" onClick={closeMenu}>
                        <span>{username}님 로그인 중</span>
                      </Link>
                      <button
                        onClick={() => {
                          closeMenu();
                          handleLogout();
                        }}
                        className="btn"
                      >
                        로그아웃
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* pc menu */}
            <div className="util" ref={utilRef}>
              <nav ref={navRef} onMouseEnter={openGnb} onMouseLeave={closeGnb}>
                <ul className="depth1_wrap">
                  <li className="depth1">
                    <h3>고객 응대</h3>
                  </li>
                  <li className="depth1">
                    <h3>트레이닝/코칭</h3>
                  </li>
                  <li className="depth1">
                    <h3>레퍼런스</h3>
                  </li>
                  <li className="depth1">
                    <h3>상담/운영</h3>
                  </li>
                </ul>

                <div className="depth2_wrap">
                  <div className="lst">
                    <div className="depth2">
                      <ul>
                        <li>
                          <Link to="/profiling" onClick={closeMenu}>
                            프로파일링
                          </Link>
                        </li>
                        <li>
                          <Link to="/review" onClick={closeMenu}>
                            리뷰 답변
                          </Link>
                        </li>
                        <li>
                          <Link to="/training-response" onClick={closeMenu}>
                            불만고객응대
                          </Link>
                        </li>
                      </ul>
                    </div>

                    <div className="depth2">
                      <ul>
                        <li>
                          <Link to="/training" onClick={closeMenu}>
                            트레이닝
                          </Link>
                        </li>
                        <li>
                          <Link to="/training-followup" onClick={closeMenu}>
                            트레이닝(후속조치)
                          </Link>
                        </li>
                        <li>
                          <Link to="/simulation" onClick={closeMenu}>
                            시뮬레이션
                          </Link>
                        </li>
                        <li>
                          <Link to="/education" onClick={closeMenu}>
                            교육챗봇
                          </Link>
                        </li>
                      </ul>
                    </div>

                    <div className="depth2">
                      <ul>
                        <li>
                          <Link to="/dictionary" onClick={closeMenu}>
                            백과사전
                          </Link>
                        </li>
                        <li>
                          <Link to="/analysis" onClick={closeMenu}>
                            행동유형분석
                          </Link>
                        </li>
                      </ul>
                    </div>

                    <div className="depth2">
                      <ul>
                        <li>
                          <Link to="/charting" onClick={closeMenu}>
                            차팅도우미
                          </Link>
                        </li>
                        <li>
                          <Link to="/smalltalk" onClick={closeMenu}>
                            스몰토크
                          </Link>
                        </li>
                        <li>
                          <Link to="/memotalk" onClick={closeMenu}>
                            메모&알림톡도우미
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </nav>

              {/* PC - 새 탭으로 여는 외부 링크는 GNB 와 분리해 버튼으로 노출 */}
              {isJayeon && (
                <div className="quick_links">
                  {slimiEnabled && (
                    <a href="#" onClick={handleSlimiClick}>
                      슬림이 설정
                    </a>
                  )}
                  <a href="#" onClick={handleEventListClick}>
                    이벤트 신청 리스트
                  </a>
                </div>
              )}

              {token && (
                <div className="btnbox">
                  <Link to="/user_edit" onClick={closeMenu}>
                    <span>{username}님 로그인 중</span>
                  </Link>
                  <button
                    onClick={() => {
                      closeMenu();
                      handleLogout();
                    }}
                    className="btn"
                  >
                    로그아웃
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}